import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Link2, Copy, Wallet2, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApp } from "@/App";
import { cn } from "@/lib/utils";

const ReferralPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);

  const handleBack = useCallback(() => {
    haptic?.("light");
    navigate(-1);
  }, [haptic, navigate]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const res = await api.get("/referral");
        setInfo(res?.data || null);
      } catch (err) {
        toast.error(t("referral.error"));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [api, t]);

  const referralLink = info?.link || "";
  const partnerRatePctNum = Number(info?.partner_rate_pct ?? 10);
  const partnerRatePct = Number.isFinite(partnerRatePctNum)
    ? Number(partnerRatePctNum.toFixed(2))
    : 10;

  const handleCopy = async () => {
    if (!referralLink) return;
    haptic?.("light");
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success(t("referral.copy_success"));
    } catch (_) {
      toast.error(t("referral.copy_error"));
    }
  };

  const formatUsd = (value) => {
    const num = Number(value ?? 0);
    if (!Number.isFinite(num)) return "0.00";
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="referral-page">
      <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
        {showInlineBack ? (
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-8 w-8 items-center justify-center text-foreground"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="h-8 w-8" />
        )}
        <h1 className="text-center text-[17px] font-semibold text-foreground">{t("referral.title")}</h1>
        <span aria-hidden="true" className="h-8 w-8" />
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-5 pb-[calc(5.5rem+var(--lp-bottom))] space-y-4">
        <div className="lp-glass p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-link" />
          </div>
          <div>
            <div className="font-semibold text-base">{t("referral.title")}</div>
            <div className="text-xs text-muted-foreground">{t("referral.subtitle")}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="lp-glass !rounded-xl p-3">
            <div className="text-xs text-muted-foreground mb-1">{t("referral.referred")}</div>
            <div className="text-xl font-semibold">{Number(info?.referred_count ?? 0)}</div>
          </div>
          <div className="lp-glass !rounded-xl p-3">
            <div className="text-xs text-muted-foreground mb-1">{t("referral.earned")}</div>
            <div className="text-xl font-semibold">${formatUsd(info?.earnings_usd_total)}</div>
          </div>
          <div className="lp-glass !rounded-xl p-3">
            <div className="text-xs text-muted-foreground mb-1">{t("referral.rate")}</div>
            <div className="text-xl font-semibold">{partnerRatePct}%</div>
          </div>
        </div>
        </div>

        <div className="lp-glass p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Link2 className="w-4 h-4" />
          {t("referral.link_title")}
        </div>
        <div className={cn(
          "lp-glass !rounded-xl w-full px-3 py-2 text-sm break-all",
          !referralLink && "text-muted-foreground"
        )}>
          {referralLink || t("referral.link_missing")}
        </div>
        <button
          onClick={handleCopy}
          disabled={!referralLink}
          className={cn(
            "w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold",
            referralLink ? "bg-primary text-primary-foreground" : "bg-[var(--lp-tile)] text-muted-foreground"
          )}
        >
          <Copy className="w-4 h-4" />
          {t("referral.copy")}
        </button>
        </div>

        <div className="lp-glass p-4">
        <div className="flex items-center gap-2 text-sm font-semibold mb-2">
          <Wallet2 className="w-4 h-4" />
          {t("referral.payouts_title")}
        </div>
        <div className="text-xs text-muted-foreground">
          {t("referral.payouts_desc")}
        </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;
